import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatPaginatorIntl, PageEvent } from '@angular/material/paginator';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime } from 'rxjs/operators';
import { NotificationService } from '../_services/notification.service';
import { GestionUsuariosService } from './gestion-usuarios.service';
import { Rol, Usuario, UsuarioStats } from './usuario.model';
import { ConfirmDeleteDialogComponent } from './dialogs/confirm-delete-dialog.component';
import { RolesDialogComponent } from './dialogs/roles-dialog.component';
import { UsuarioFormDialogComponent, UsuarioFormResult } from './dialogs/usuario-form-dialog.component';

@Component({
  selector: 'app-gestion-usuarios',
  templateUrl: './gestion-usuarios.component.html',
  styleUrls: ['./gestion-usuarios.component.scss']
})
export class GestionUsuariosComponent implements OnInit {

  @ViewChild(MatPaginator) paginator: MatPaginator;

  displayedColumns: string[] = ['usuario', 'documento', 'email', 'roles', 'estado', 'alta', 'acciones'];

  usuarios: Usuario[] = [];
  rolesDisponibles: Rol[] = [];
  stats: UsuarioStats = { total: 0, activos: 0, inactivos: 0, rolesConfigurados: 0 };

  filtroBusqueda = new FormControl('');
  filtroEstado = new FormControl<string>('todos');
  filtroRol = new FormControl<number | 'todos'>('todos');

  page = 0;
  size = 5;
  totalElements = 0;
  loading = false;

  constructor(
    private service: GestionUsuariosService,
    private notif: NotificationService,
    private dialog: MatDialog,
    private paginatorIntl: MatPaginatorIntl,
    private translate: TranslateService
  ) {
    this.traduccionPaginator();
  }

  ngOnInit(): void {
    this.cargaRoles();
    this.cargaStats();
    this.cargaUsuarios();

    this.filtroBusqueda.valueChanges.pipe(debounceTime(350)).subscribe(() => {
      this.page = 0;
      this.cargaUsuarios();
    });
    this.filtroEstado.valueChanges.subscribe(() => {
      this.page = 0;
      this.cargaUsuarios();
    });
    this.filtroRol.valueChanges.subscribe(() => {
      this.page = 0;
      this.cargaUsuarios();
    });
  }

  cargaRoles(): void {
    this.service.listRoles().subscribe({
      next: (data) => { this.rolesDisponibles = data || []; },
      error: () => {
        this.notif.error(
          this.translate.instant('COMMON.ERROR'),
          this.translate.instant('USUARIOS.MSG.LOAD_ROLES'),
          3000
        );
      }
    });
  }

  cargaStats(): void {
    this.service.stats().subscribe({
      next: (data) => { this.stats = data; },
      error: () => {}
    });
  }

  cargaUsuarios(): void {
    this.loading = true;
    const filtro = {
      page: this.page,
      size: this.size,
      search: this.filtroBusqueda.value || '',
      rolId: this.filtroRol.value === 'todos' ? undefined : Number(this.filtroRol.value),
      activo: this.estadoToBoolean(this.filtroEstado.value || 'todos'),
    };

    this.service.list(filtro).subscribe({
      next: (resp) => {
        this.usuarios = resp.data || [];
        this.totalElements = resp.totalElements || 0;
        this.loading = false;
      },
      error: () => {
        this.notif.error(
          this.translate.instant('COMMON.ERROR'),
          this.translate.instant('USUARIOS.MSG.LOAD_USERS'),
          3000
        );
        this.loading = false;
      }
    });
  }

  private estadoToBoolean(estado: string): boolean | undefined {
    if (estado === 'activos') return true;
    if (estado === 'inactivos') return false;
    return undefined;
  }

  onPageChange(ev: PageEvent): void {
    this.page = ev.pageIndex;
    this.size = ev.pageSize;
    this.cargaUsuarios();
  }

  iniciales(u: Usuario): string {
    const a = (u.nombre || '').trim().charAt(0);
    const b = (u.apellido1 || '').trim().charAt(0);
    return (a + b).toUpperCase();
  }

  formatFecha(value: string | number[] | undefined): string {
    if (!value) return '';
    let date: Date;
    if (Array.isArray(value)) {
      const [y, m, d] = value;
      date = new Date(y, (m || 1) - 1, d || 1);
    } else {
      date = new Date(value);
    }
    if (isNaN(date.getTime())) return '';
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${date.getFullYear()}`;
  }

  abrirCrear(): void {
    const ref = this.dialog.open(UsuarioFormDialogComponent, {
      data: { rolesDisponibles: this.rolesDisponibles },
      panelClass: 'gu-dialog-panel'
    });
    ref.afterClosed().subscribe((result: UsuarioFormResult | null) => {
      if (!result || result.mode !== 'create') return;
      this.service.create(result.payload).subscribe({
        next: () => {
          this.notif.success(
            this.translate.instant('COMMON.DONE'),
            this.translate.instant('USUARIOS.MSG.CREATED'),
            2800
          );
          this.cargaStats();
          this.cargaUsuarios();
        },
        error: (err) => {
          const msg = err?.error?.message || this.translate.instant('COMMON.ERROR');
          this.notif.error(this.translate.instant('COMMON.ERROR'), msg, 3500);
        }
      });
    });
  }

  abrirEditar(u: Usuario): void {
    const ref = this.dialog.open(UsuarioFormDialogComponent, {
      data: { usuario: u, rolesDisponibles: this.rolesDisponibles },
      panelClass: 'gu-dialog-panel'
    });
    ref.afterClosed().subscribe((result: UsuarioFormResult | null) => {
      if (!result || result.mode !== 'edit') return;
      this.aplicarEdicion(u, result);
    });
  }

  private aplicarEdicion(u: Usuario, result: UsuarioFormResult): void {
    const updateBody = { ...result.payload };
    delete updateBody.password;
    delete updateBody.roles;

    this.service.update(u.id, updateBody).subscribe({
      next: () => {
        const tasks: Promise<any>[] = [];
        if (result.passwordChanged) {
          tasks.push(this.service.updatePassword(u.id, result.payload.password).toPromise());
        }
        if (result.rolesChanged) {
          tasks.push(this.service.setRoles(u.id, result.selectedRoles || []).toPromise());
        }
        if (result.activoChanged) {
          const op = result.newActivo ? this.service.reactivar(u.id) : this.service.desactivar(u.id);
          tasks.push(op.toPromise());
        }
        Promise.all(tasks).then(() => {
          this.notif.success(
            this.translate.instant('COMMON.DONE'),
            this.translate.instant('USUARIOS.MSG.UPDATED'),
            2800
          );
          this.cargaStats();
          this.cargaUsuarios();
        }).catch((err) => {
          const msg = err?.error?.message || this.translate.instant('COMMON.WARNING');
          this.notif.warning(this.translate.instant('COMMON.WARNING'), msg, 3500);
          this.cargaUsuarios();
        });
      },
      error: (err) => {
        const msg = err?.error?.message || this.translate.instant('COMMON.ERROR');
        this.notif.error(this.translate.instant('COMMON.ERROR'), msg, 3500);
      }
    });
  }

  abrirRoles(u: Usuario): void {
    const ref = this.dialog.open(RolesDialogComponent, {
      data: { usuario: u, rolesDisponibles: this.rolesDisponibles },
      panelClass: 'gu-dialog-panel'
    });
    ref.afterClosed().subscribe((roles: number[] | null) => {
      if (!roles) return;
      this.service.setRoles(u.id, roles).subscribe({
        next: () => {
          this.notif.success(
            this.translate.instant('COMMON.DONE'),
            this.translate.instant('USUARIOS.MSG.ROLES_UPDATED'),
            2800
          );
          this.cargaUsuarios();
        },
        error: (err) => {
          const msg = err?.error?.message || this.translate.instant('COMMON.ERROR');
          this.notif.error(this.translate.instant('COMMON.ERROR'), msg, 3500);
        }
      });
    });
  }

  toggleActivo(u: Usuario): void {
    const op = u.activo ? this.service.desactivar(u.id) : this.service.reactivar(u.id);
    op.subscribe({
      next: () => {
        this.notif.success(
          this.translate.instant('COMMON.DONE'),
          this.translate.instant('USUARIOS.MSG.USER_STATUS', {
            accion: this.translate.instant(u.activo ? 'USUARIOS.MSG.DEACTIVATED' : 'USUARIOS.MSG.REACTIVATED')
          }),
          2800
        );
        this.cargaStats();
        this.cargaUsuarios();
      },
      error: (err) => {
        const msg = err?.error?.message || this.translate.instant('USUARIOS.MSG.STATUS_FAIL');
        this.notif.error(this.translate.instant('COMMON.ERROR'), msg, 3500);
      }
    });
  }

  confirmarEliminar(u: Usuario): void {
    const ref = this.dialog.open(ConfirmDeleteDialogComponent, {
      data: {
        title: this.translate.instant('USUARIOS.MSG.DELETE_TITLE'),
        message: this.translate.instant('USUARIOS.MSG.DELETE_MSG', { nombre: u.nombre, apellido: u.apellido1 }),
        confirmLabel: this.translate.instant('COMMON.DELETE')
      },
      panelClass: 'gu-dialog-panel'
    });
    ref.afterClosed().subscribe((ok: boolean) => {
      if (!ok) return;
      this.service.desactivar(u.id).subscribe({
        next: () => {
          this.notif.success(
            this.translate.instant('COMMON.DONE'),
            this.translate.instant('USUARIOS.MSG.DELETED'),
            2800
          );
          this.cargaStats();
          this.cargaUsuarios();
        },
        error: (err) => {
          const msg = err?.error?.message || this.translate.instant('USUARIOS.MSG.DELETE_FAIL');
          this.notif.error(this.translate.instant('COMMON.ERROR'), msg, 3500);
        }
      });
    });
  }

  forzarConfirmacion(u: Usuario): void {
    this.service.forzarConfirmacionEmail(u.id).subscribe({
      next: () => {
        this.notif.success(
          this.translate.instant('COMMON.DONE'),
          this.translate.instant('USUARIOS.MSG.EMAIL_SENT', { email: u.email }),
          3000
        );
        this.cargaUsuarios();
      },
      error: (err) => {
        const msg = err?.error?.message || this.translate.instant('USUARIOS.MSG.EMAIL_FAIL');
        this.notif.error(this.translate.instant('COMMON.ERROR'), msg, 3500);
      }
    });
  }

  rolesPreview(u: Usuario): Rol[] { return u.rol || []; }

  private traduccionPaginator(): void {
    this.paginatorIntl.itemsPerPageLabel = this.translate.instant('PAGINATOR.ITEMS_PER_PAGE');
    this.paginatorIntl.firstPageLabel = '';
    this.paginatorIntl.previousPageLabel = '';
    this.paginatorIntl.nextPageLabel = '';
    this.paginatorIntl.lastPageLabel = '';
    this.paginatorIntl.getRangeLabel = (page, pageSize, length) => {
      if (length === 0 || pageSize === 0) {
        return this.translate.instant('PAGINATOR.RANGE_EMPTY', { length });
      }
      length = Math.max(length, 0);
      const startIndex = page * pageSize;
      const endIndex = startIndex < length ? Math.min(startIndex + pageSize, length) : startIndex + pageSize;
      return this.translate.instant('PAGINATOR.RANGE', { start: startIndex + 1, end: endIndex, length });
    };
  }
}
